import React from 'react';
import clsx from 'clsx';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { interpolateHexColors } from '../services/colors-interpolator';
import UserIcon from '../icons/UserIcon';
import TeamIcon from '../icons/TeamIcon';
import ChartMarker from '../atoms/ChartMarker';
import { getTeamLogo } from '../atoms/TeamLogo';
import ChartImageMarker from '../atoms/ChartImageMarker';
import ChartLabel from '../atoms/ChartLabel';
import { getScoreValue } from '../atoms/Score';
import Team from '../types/Team';
import Player from '../types/Player';
import Position from '../types/Position';
import { RanksType } from '../atoms/Ranks';
import { getScoreTooltip } from '../services/score';

interface CombineChartProps {
  className?: string;
  team?: Team;
  player?: Player;
  position?: Position;
}

const START_COLOR = '#ffe084';
const END_COLOR = '#408a06';

const averages = [
  {
    name: 'FCS',
    value: 2.0,
  },
  {
    name: 'GROUP_OF_5',
    value: 2.5,
  },
  {
    name: 'POWER_5',
    value: 3.0,
  }
];

const useStyles = makeStyles((theme: Theme) => ({
  combineChart: {
    width: '100%',
    height: '100%',
    padding: theme.spacing(4),
    boxSizing: 'border-box',
  },
  chart: {
    width: '100%',
    height: '100%',
    position: 'relative',
  },
  axis: {
    width: '100%',
    height: '4px',
    background: `linear-gradient(to right, ${START_COLOR}, ${END_COLOR})`,
    position: 'absolute',
    top: '50%',
    left: 0,
    transform: 'translateY(-50%)',
  },
}), { name: CombineChart.name });

export default function CombineChart (props: CombineChartProps) {
  const {
    className,
    team,
    player,
    position,
  } = props;
  const classes = useStyles();

  let teamMarker = null;
  if (!!team?.combine?.length) {
    const teamCombine = getScoreValue(team.combine, { position }) || 0;
    const teamPosition = Math.round(teamCombine * 100 / 5);

    teamMarker = (
      <ChartImageMarker
        position={teamPosition}
        image={getTeamLogo(team)}
        iconFallback={TeamIcon}
        tooltipText={`Team Avg. Com.: ${teamCombine.toFixed(2)}`}
        color={interpolateHexColors(START_COLOR, END_COLOR, teamPosition)}
      />
    );
  }

  let playerMarker = null;
  if (!!player?.combine?.length) {
    const combine = (getScoreValue(player.combine, { position, combines: player.hsCombines }) || 0).toFixed(2);
    const playerPosition = Math.round(parseFloat(combine) * 100 / 5);
    // const tooltipTextold = `${player.firstName} ${player.lastName} Com.: ${combine}`;
    const tooltipText = getScoreTooltip(RanksType.COMBINE, parseFloat(combine));
    playerMarker = (
      <ChartImageMarker
        position={playerPosition}
        image={player.photoUrl}
        iconFallback={UserIcon}
        tooltipText={tooltipText}
        color={interpolateHexColors(START_COLOR, END_COLOR, playerPosition)}
        avatar
      />
    );
  }

  return (
    <div className={clsx(classes.combineChart, className)}>
      <div className={classes.chart}>
        <div className={classes.axis} />

        <ChartMarker
          position={0}
          label='0'
          color={START_COLOR}
        />
        <ChartMarker
          position={20}
          label='1.0'
          color={interpolateHexColors(START_COLOR, END_COLOR, 20)}
        />
        <ChartMarker
          position={40}
          label='2.0'
          color={interpolateHexColors(START_COLOR, END_COLOR, 40)}
        />
        <ChartMarker
          position={50}
          label='2.5'
          color={interpolateHexColors(START_COLOR, END_COLOR, 50)}
        />
        <ChartMarker
          position={60}
          label='3.0'
          color={interpolateHexColors(START_COLOR, END_COLOR, 60)}
        />
        <ChartMarker
          position={80}
          label='4.0'
          color={interpolateHexColors(START_COLOR, END_COLOR, 80)}
        />
        <ChartMarker
          position={100}
          label='5.0'
          color={END_COLOR}
        />

        {averages.map((average:any, index) => {
          const position = Math.round(average.value * 100 / 5);
          const name = average.name
            .split('_')
            .map((word:string, index:number) => (
              <span key={index}>{word}&nbsp;</span>
            ));

          return (
            <ChartLabel
              key={index}
              position={position}
              label={(<>{name}<br />AVG</>)}
              color={interpolateHexColors(START_COLOR, END_COLOR, position)}
              long={(index + 1) % 2 === 0}
            />
          );
        })}

        {teamMarker}
        {playerMarker}
      </div>
    </div>
  );
}
